import { useState, useEffect, useRef, useCallback } from 'react';

import { getCallByExternalKey } from '../api';

const INITIAL_DELAY = 3000;
const BASE_INTERVAL = 2000;
const MAX_RETRIES = 3;

type UseCallPollingReturn = {
  foundCallId: number | null;
  isPolling: boolean;
  error: string | null;
};

/**
 * Hook for polling to find a call by its external key (SID).
 * Uses exponential backoff strategy.
 */
const useCallPolling = (sid: string | undefined, enabled: boolean = true): UseCallPollingReturn => {
  const [foundCallId, setFoundCallId] = useState<number | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const poll = useCallback(async(attempt: number) => {
    if (!isMountedRef.current || !sid) return;

    try {
      const response = await getCallByExternalKey(sid);

      if (isMountedRef.current) {
        setFoundCallId(response.id);
        setIsPolling(false);
      }
      return;
    } catch (err) {
      // 404 means call record not created yet - continue polling
      if (err instanceof Error && err.message.includes('404')) {
        if (attempt >= MAX_RETRIES) {
          if (isMountedRef.current) {
            setError('Call record not found. Please refresh the page.');
            setIsPolling(false);
          }
          return;
        }

        // Schedule next poll with exponential backoff
        const nextInterval = BASE_INTERVAL * Math.pow(2, attempt);
        timeoutRef.current = window.setTimeout(() => {
          poll(attempt + 1);
        }, nextInterval);
        return;
      }

      // Other errors - report and stop
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch call data');
        setIsPolling(false);
      }
    }
  }, [sid]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!sid || !enabled || foundCallId) {
      return cleanup;
    }

    setIsPolling(true);
    setError(null);

    // Start polling after initial delay
    timeoutRef.current = window.setTimeout(() => {
      poll(0);
    }, INITIAL_DELAY);

    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [sid, enabled, foundCallId, poll, cleanup]);

  return {
    foundCallId,
    isPolling,
    error,
  };
};

export default useCallPolling;
